import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	ArrowLeft,
	Calendar,
	Clock,
	MoreHorizontal,
	PanelRightClose,
	PanelRightOpen,
	Trash2,
	User,
	Users,
	Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteTranscript } from "@/hooks/transcripts/useTranscriptMutations";
import { formatFullDateTime } from "@/components/inbox/utils";
import { formatDuration } from "./utils";
import type { Transcript } from "@/types/transcript";

export interface TranscriptThreadHeaderProps {
	transcript: Transcript;
	onBack?: () => void;
	contextPanelOpen?: boolean;
	onToggleContextPanel?: () => void;
	onDeleted: () => void;
}

/** Sticky header above the transcript: subject, meeting metadata, actions. */
export const TranscriptThreadHeader: React.FC<TranscriptThreadHeaderProps> = ({
	transcript,
	onBack,
	contextPanelOpen,
	onToggleContextPanel,
	onDeleted,
}) => {
	const { t, i18n } = useTranslation();
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const deleteMutation = useDeleteTranscript();

	const duration = formatDuration(transcript.start_time, transcript.end_time);

	const handleDelete = () => {
		deleteMutation.mutate(transcript.id, {
			onSuccess: () => {
				setConfirmDeleteOpen(false);
				onDeleted();
			},
		});
	};

	return (
		<header className="flex shrink-0 items-start gap-3 border-b border-border bg-background px-4 py-3">
			{onBack && (
				<Button
					variant="ghost"
					size="icon"
					className="mt-0.5 h-8 w-8 shrink-0 -ml-1"
					onClick={onBack}
					aria-label={t("transcripts.back", "Back")}
				>
					<ArrowLeft className="h-4 w-4" />
				</Button>
			)}
			<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-muted/30">
				<Video className="h-4 w-4 text-muted-foreground" aria-hidden />
			</div>
			<div className="min-w-0 flex-1">
				<h2 className="truncate text-sm font-semibold text-foreground">
					{transcript.subject || t("transcripts.untitled", "Untitled Meeting")}
				</h2>
				<div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
					{transcript.start_time && (
						<span className="flex items-center gap-1 tabular-nums">
							<Calendar className="h-3 w-3" aria-hidden />
							{formatFullDateTime(transcript.start_time, i18n.language)}
						</span>
					)}
					{duration && (
						<span className="flex items-center gap-1 tabular-nums">
							<Clock className="h-3 w-3" aria-hidden />
							{duration}
						</span>
					)}
					{transcript.participants?.length > 0 && (
						<span className="flex items-center gap-1">
							<Users className="h-3 w-3" aria-hidden />
							{t("transcripts.participantCount", "{{count}} participants", {
								count: transcript.participants.length,
							})}
						</span>
					)}
					{transcript.organizer_email && (
						<span className="flex min-w-0 items-center gap-1">
							<User className="h-3 w-3 shrink-0" aria-hidden />
							<span className="truncate">{transcript.organizer_email}</span>
						</span>
					)}
				</div>
			</div>
			<div className="flex shrink-0 items-center gap-1">
				{onToggleContextPanel && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7 text-muted-foreground"
								onClick={onToggleContextPanel}
								aria-label={t("transcripts.toggleContext", "Toggle details panel")}
							>
								{contextPanelOpen ? (
									<PanelRightClose className="h-4 w-4" />
								) : (
									<PanelRightOpen className="h-4 w-4" />
								)}
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">
							{t("transcripts.toggleContext", "Toggle details panel")}
						</TooltipContent>
					</Tooltip>
				)}
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7 text-muted-foreground"
							aria-label={t("common.moreActions", "More actions")}
						>
							<MoreHorizontal className="h-4 w-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							className="text-destructive focus:text-destructive"
							onClick={() => setConfirmDeleteOpen(true)}
						>
							<Trash2 className="mr-2 h-4 w-4" />
							{t("transcripts.delete", "Delete transcript")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			<AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("transcripts.deleteConfirmTitle", "Delete transcript?")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t(
								"transcripts.deleteConfirmDescription",
								"This also removes its candidate links and AI analysis. This can't be undone."
							)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={(event) => {
								event.preventDefault();
								handleDelete();
							}}
							disabled={deleteMutation.isPending}
						>
							{t("transcripts.delete", "Delete transcript")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</header>
	);
};
